import {IconButton} from '@wandb/components';
import React, {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  assemblePromptParts,
  draftKey,
  useDraftStore,
} from '../../agentChat/composer';
import {buildAssembledPrompt} from '../../agentChat/promptAssembly';
import {useActiveThread} from '../../agentChat/store';
import {CodeSnippet} from '../CodeSnippet';
import {useNavContext} from '../NavContextProvider';
import {internalTooltip, useIsInternalUser} from './internalTools';

interface DebugViewState {
  open: boolean;
  toggle: () => void;
}

const WBAgentDebugViewContext = createContext<DebugViewState>({
  open: false,
  toggle: () => {},
});

/**
 * WBAgentDebugViewProvider holds the agent's debug view context and shares it
 * with the toggle button and the view slot.
 */
export const WBAgentDebugViewProvider = ({children}: {children: ReactNode}) => {
  const [open, setOpen] = useState(false);
  const state = useMemo<DebugViewState>(
    () => ({open, toggle: () => setOpen(v => !v)}),
    [open]
  );

  return (
    <WBAgentDebugViewContext.Provider value={state}>
      {children}
    </WBAgentDebugViewContext.Provider>
  );
};

/**
 * WBAgentDebugViewToggle is an internal-only button that toggles the agent's
 * assembled-prompt debug view.
 */
export const WBAgentDebugViewToggle = () => {
  const {open, toggle} = useContext(WBAgentDebugViewContext);
  const isInternal = useIsInternalUser();

  return isInternal ? (
    <IconButton
      variant="ghost"
      icon="bug"
      isActive={open}
      aria-label="toggle assembled-prompt debug"
      onClick={toggle}
      tooltip={{
        children: internalTooltip(
          open ? 'Show messages' : 'Show assembled prompt'
        ),
      }}
    />
  ) : null;
};

/**
 * WBAgentDebugViewSlot renders the debug view in place of `children` (e.g.
 * thread messages) while the debug view is open, and the children otherwise.
 */
export const WBAgentDebugViewSlot = ({children}: {children: ReactNode}) => {
  const {open} = useContext(WBAgentDebugViewContext);
  return open ? <WBAgentDebugView /> : <>{children}</>;
};

const WBAgentDebugView = () => (
  <div className="flex-1 p-8">
    <WBAgentAssembledPromptView />
  </div>
);

/**
 * WBAgentAssembledPromptView renders a JSON view of the assembled prompt that
 * would be sent to the wb_agent backend to create the next turn.
 */
const WBAgentAssembledPromptView = () => {
  const {params} = useNavContext();
  const activeThread = useActiveThread();
  const [json, setJson] = useState('');

  const paramsRef = useRef(params);
  paramsRef.current = params;
  const threadIdRef = useRef<string | null>(activeThread?.rootTurnId ?? null);
  threadIdRef.current = activeThread?.rootTurnId ?? null;

  useEffect(() => {
    let raf = 0;

    const evaluate = () => {
      const draft =
        useDraftStore.getState().drafts[draftKey(threadIdRef.current)];
      const userParts = draft ? assemblePromptParts(draft) : [];
      const prompt = buildAssembledPrompt(userParts, paramsRef.current);

      // Re-render only when the assembled prompt actually changes.
      const next = JSON.stringify(prompt, null, 2);
      setJson(prev => (prev === next ? prev : next));

      raf = requestAnimationFrame(evaluate);
    };

    evaluate();
    return () => cancelAnimationFrame(raf);
  }, []);

  return <CodeSnippet codeSnippet={json} codeLanguage="json" />;
};
